'use client'

import { useRef, useState } from 'react'
import { Play, Volume2, VolumeX } from 'lucide-react'

import { MINIMAX_H3_CDN } from '@/content/marketing/minimax-h3-prompts/prompts'
import { H3_CLIPS } from './minimax-h3-gallery'

/**
 * Sample clips for the studio's result column, shown before anything is run.
 *
 * They were three of the page's own edit teardowns — a serum bottle, a studio
 * portrait — which are the right evidence for H3's differentiator but read as
 * muted stock at thumbnail size, on the one screen that has to earn the scroll.
 * These are the first three of the gallery wall instead: each renders type a
 * visitor can read at a glance (a product headline, a HUD button, title cards),
 * which is the fastest way to show a video model doing something unusual.
 *
 * Sourced from H3_CLIPS rather than re-listed, so the wall and this column
 * cannot drift into showing different things under the same claim.
 */

const demos = H3_CLIPS.slice(0, 3)

export function MinimaxH3StudioDemos() {
  const videoRefs = useRef<(HTMLVideoElement | null)[]>([])
  const [soundOn, setSoundOn] = useState<number | null>(null)

  const enter = (i: number) => {
    const v = videoRefs.current[i]
    if (v) void v.play().catch(() => {})
  }

  const leave = (i: number) => {
    const v = videoRefs.current[i]
    if (!v) return
    v.pause()
    v.currentTime = 0
  }

  const toggleSound = (i: number) => {
    const next = soundOn === i ? null : i
    setSoundOn(next)
    videoRefs.current.forEach((v, idx) => {
      if (!v) return
      v.muted = idx !== next
      if (idx === next) void v.play().catch(() => {})
    })
  }

  return (
    <div className="flex flex-col gap-3">
      <p className="text-muted-foreground text-xs">
        Made with H3 — hover to play. Your own result replaces these.
      </p>
      {demos.map((d, i) => (
        <div
          key={d.file}
          className="group border-border bg-card overflow-hidden rounded-xl border"
          onMouseEnter={() => enter(i)}
          onMouseLeave={() => leave(i)}
        >
          <div className={`relative overflow-hidden bg-black ${d.aspect}`}>
            <video
              ref={el => {
                videoRefs.current[i] = el
              }}
              src={`${MINIMAX_H3_CDN}/${d.file}.mp4`}
              poster={`${MINIMAX_H3_CDN}/${d.file}.jpg`}
              muted
              loop
              playsInline
              preload="metadata"
              className="h-full w-full object-cover"
            />
            <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-black/15 transition-opacity group-hover:opacity-0">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-black/55 px-2.5 py-1 text-[11px] font-medium text-white">
                <Play className="size-3" /> Play
              </span>
            </div>
            <button
              type="button"
              aria-label={soundOn === i ? 'Mute' : 'Unmute'}
              className="absolute right-2 bottom-2 rounded-full bg-black/55 p-1.5 text-white/90 transition-colors hover:bg-black/75"
              onClick={() => toggleSound(i)}
            >
              {soundOn === i ? (
                <Volume2 className="size-3.5" />
              ) : (
                <VolumeX className="size-3.5" />
              )}
            </button>
          </div>
          <div className="p-3">
            <h3 className="text-foreground text-xs font-semibold">{d.label}</h3>
            <p className="text-muted-foreground mt-1 text-[11px] leading-4">
              {d.body}
            </p>
          </div>
        </div>
      ))}
    </div>
  )
}
